import { useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Spin } from 'antd';
import { capitalizeFirstLetter } from '../../utils/common.util';
import { SMSChatContext } from './SMSChatContext';
import { getSMSConversationsService } from '../../services/SMSConversation/index.service';
import moment from 'moment';
import { renderText } from './utils';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

// Constants
const PER_PAGE = 20;

const UserAvatar = ({ customer }) => {
    const [imgError, setImgError] = useState(false);
    const { profilePictureUrl, firstName, lastName } = customer || {};
    
    // Reset error when url changes (recycling list items)
    useEffect(() => {
        setImgError(false);
    }, [profilePictureUrl]);

    if (!imgError && profilePictureUrl) {
        return (
            <img 
                src={profilePictureUrl} 
                alt="User" 
                className="w-100 h-100 rounded-circle object-cover"
                onError={() => setImgError(true)}
            />
        );
    }

    return (
        <span>
            {firstName ? firstName.charAt(0).toUpperCase() : '#'}
        </span>
    );
};

const SMSChatSidebar = ({ channelType }) => {
  const { t } = useTranslation();
  const {
    conversationsList,
    setConversationsList,
    selectedConversation,
    setSelectedConversation,
    isLoadingConversations,
    setIsLoadingConversations,
    hasMoreConversations,
    setHasMoreConversations,
    conversationsPage,
    setConversationsPage,
    search,
    setSearch,
  } = useContext(SMSChatContext);

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [filterUserType, setFilterUserType] = useState('customer');

  // Fetch Conversations
  const fetchConversations = useCallback(async (isReset = false) => {
    if (isReset) {
      setIsLoadingConversations(true);
    }
    
    try {
      const page = isReset ? 1 : conversationsPage;
      const offset = (page - 1) * PER_PAGE;
      
      const queryParams = {
        channelType,
        userType: filterUserType,
        limit: PER_PAGE,
        offset,
        search,
      };

      const response = await getSMSConversationsService({ queryParams });
      
      if (response?.success) {
        const { rows, count } = response.data;
        
        if (isReset) {
          setConversationsList(rows);
          setConversationsPage(2); // Next page
        } else {
             // Append only unique items
            setConversationsList((prev) => {
                 const newItems = rows.filter(r => !prev.find(p => p.id === r.id));
                 return [...prev, ...newItems];
            });
            setConversationsPage((prev) => prev + 1);
        }

        setHasMoreConversations(offset + rows.length < count);

      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingConversations(false);
      setIsFirstLoad(false);
    }
  }, [channelType, filterUserType, conversationsPage, search, conversationsList.length, setConversationsList, setHasMoreConversations, setIsLoadingConversations, setConversationsPage]);

  // Initial Load & Search & Filter Change
  useEffect(() => {
    // Clear old conversation when switching Booking <-> Marketing or Customer <-> Provider
    setSelectedConversation(null);

    // Reset conversations list & pagination (safe)
    setConversationsList([]);
    setConversationsPage(1);

    // Fetch conversations for new channel/filter
    fetchConversations(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelType, search, filterUserType]);


  const handleSearch = (e) => {
      setSearch(e.target.value);
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    // Optimistic unread clear
    setConversationsList(prev => prev.map(c => c.id === conversation.id ? { ...c, unreadCount: 0 } : c));
  };
  
  const getDisplayName = (conv) => {
        if (conv.Customer) {
            return `${capitalizeFirstLetter(conv.Customer.firstName)} ${capitalizeFirstLetter(conv.Customer.lastName)}`;
        }
        return conv.customerPhone;
  };


  return (
    <div className="nk-chat-aside position-relative">

      <div className="nk-chat-aside-head">
        <div className="nk-chat-aside-search px-0 w-100 mb-0">
             <Input
                placeholder="Search by name or phone"
                size="large"
                prefix={<SearchOutlined />}
                value={search}
                onChange={handleSearch}
                className="form-round"
             />

            <div className="d-flex w-100 mt-2">
                <div 
                    className={`flex-fill text-center p-2 cursor-pointer ${filterUserType === 'customer' ? 'border-bottom border-primary text-primary fw-bold' : 'text-muted'}`}
                    onClick={() => setFilterUserType('customer')}
                >
                    Customer
                </div>
                <div 
                    className={`flex-fill text-center p-2 cursor-pointer ${filterUserType === 'provider' ? 'border-bottom border-primary text-primary fw-bold' : 'text-muted'}`}
                    onClick={() => setFilterUserType('provider')}
                >
                    Provider
                </div>
            </div>

        </div>
      </div>

      {isLoadingConversations && isFirstLoad ? (
         <Spin
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              height: '100%',
            }}
          />
      ) : conversationsList.length > 0 ? (
        <div className="nk-chat-aside-body d-flex flex-column">
             <div className="nk-chat-list">
                {/* <h6 className="title overline-title-alt">
                  Messages
                </h6> */}
                <div
                  id="sidebarScrollableDiv"
                  className="chat-list"
                  style={{
                    height: 'calc(100vh - 240px)',
                    overflow: 'auto',
                  }}
                >
                    <InfiniteScroll
                        dataLength={conversationsList.length}
                        next={() => fetchConversations(false)}
                        hasMore={hasMoreConversations}
                        loader={<Spin style={{ display: 'flex', margin: 'auto' }} />}
                        scrollableTarget="sidebarScrollableDiv"
                    >
                        {conversationsList.map((conv) => (
                            <div
                                key={conv.id}
                                className={`chat-item ${
                                  selectedConversation?.id === conv.id
                                    ? 'current'
                                    : conv.unreadCount > 0
                                    ? 'is-unread'
                                    : ''
                                }`}
                                onClick={() => handleSelectConversation(conv)}
                            >
                                <div className="chat-link">
                                    <div className={`chat-media user-avatar ${!conv.Customer?.profilePictureUrl ? 'bg-purple' : ''}`}>
                                        <UserAvatar customer={conv.Customer} />
                                    </div>
                                    <div className="chat-info">
                                        <div className="chat-from">
                                            <div className="name">
                                                {getDisplayName(conv)}
                                            </div>
                                              {/* <span className="time" style={{ display: 'flex', flexDirection: 'column' }}> */}
                                                {/* <span style={{ color: '#f47321' }}>{conv.userType === 'customer' ? 'Customer' : 'Provider'}</span> */}
                                               <span className="time"> {moment(conv.lastMessageAt).fromNow()} </span>
                                              {/* </span> */}
                                        </div>
                                        <div className="chat-context">
                                            <div className="text ml-0">
                                                <p>{renderText(conv.lastMessagePreview)}</p>

                                            </div>
                                            {conv.unreadCount > 0 && (
                                                <span className="badge bg-secondary">
                                                    {conv.unreadCount}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </InfiniteScroll>
                </div>
             </div>
        </div>
      ) : (
          <div className="noChatData noChatData-sm d-flex flex-column align-items-center justify-content-center w-100 h-100">
            <img src="/assets/images/admin/chat-img.svg" alt="chat" />
            <h4>{t('text.chat.noConversationAvailable')}</h4>
          </div>
      )}
    </div>
  );
};

export default SMSChatSidebar;
